import React, { useState, useEffect } from "react"
import HOC from "../component/HOC"
import BaseUrl from "../BaseUrl";
import axios from 'axios'
import swal from "sweetalert";
import { useProfile } from "../context/ProfileContext";
import { FiCamera, FiMail, FiPhone, FiSave, FiUser } from "react-icons/fi";

const NAME_INVALID_MESSAGE = "Only letters, spaces, hyphens, and apostrophes are allowed.";
const MOBILE_INVALID_MESSAGE = "Mobile number must contain only 7 to 15 digits.";
const nameRegex = /^[A-Za-z][A-Za-z\s'-]*$/;
const mobileRegex = /^\d{7,15}$/;

const sanitizeName = (value) => value.replace(/[^A-Za-z\s'-]/g, '');
const sanitizeMobile = (value) => value.replace(/\D/g, '').slice(0, 15);

const Profile = () => {
    const { profile, fetchProfile, setProfile } = useProfile();
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [mobile, setMobile] = useState('');
    const [image, setImage] = useState({ preview: "", raw: "" });
    const [errors, setErrors] = useState({
        firstName: '',
        lastName: '',
        mobile: '',
    });

    const syncFormFromProfile = (data) => {
        if (!data) return;
        setFirstName(sanitizeName(data.firstName || ''));
        setLastName(sanitizeName(data.lastName || ''));
        setEmail(data.email || '');
        setMobile(sanitizeMobile(data.mobile || ''));
    };

    const getProfile = async (force = false) => {
        const data = await fetchProfile(force);
        syncFormFromProfile(data);
    };

    const handleChange = e => {
        setImage({
            preview: URL.createObjectURL(e.target.files[0]),
            raw: e.target.files[0]
        });
    };

    const updateProfile = async (event) => {
        const token = await localStorage.getItem("authorization")
        var auth = {
            headers: {
                authorization: `bearer ${token}`,
            },
        };
        event.preventDefault()

        const nextErrors = {
            firstName: '',
            lastName: '',
            mobile: '',
        };
        const trimmedFirstName = firstName.trim();
        const trimmedLastName = lastName.trim();

        if (!trimmedFirstName) {
            nextErrors.firstName = "First name is required.";
        } else if (!nameRegex.test(trimmedFirstName)) {
            nextErrors.firstName = NAME_INVALID_MESSAGE;
        }

        if (!trimmedLastName) {
            nextErrors.lastName = "Last name is required.";
        } else if (!nameRegex.test(trimmedLastName)) {
            nextErrors.lastName = NAME_INVALID_MESSAGE;
        }

        if (!mobileRegex.test(mobile)) {
            nextErrors.mobile = MOBILE_INVALID_MESSAGE;
        }

        setErrors(nextErrors);

        if (nextErrors.firstName || nextErrors.lastName || nextErrors.mobile) {
            return;
        }

        const url = BaseUrl() + "api/user/update-user-profile";
        const fd = new FormData();
        fd.append("firstName", trimmedFirstName);
        fd.append("lastName", trimmedLastName);
        fd.append("mobile", mobile);
        fd.append("profile_pic", image.raw);
        try {
            const res = await axios.post(url, fd, auth);
            if (res.data.ResponseCode === 200) {
                const updated = {
                    ...profile,
                    firstName: trimmedFirstName,
                    lastName: trimmedLastName,
                    mobile,
                    fullName: `${trimmedFirstName} ${trimmedLastName}`.trim(),
                };
                if (res.data.Response) {
                    setProfile({ ...updated, ...res.data.Response });
                } else {
                    setProfile(updated);
                }
                swal("Profile updated successfully", {
                    icon: "success"
                });
            }
        } catch (err) {
            console.log("err", err);
        }
    };

    useEffect(() => {
        if (profile?.firstName) {
            syncFormFromProfile(profile);
        } else {
            getProfile();
        }
        // Keep the original mount-only profile hydration behavior.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <main className="mx-auto flex w-full max-w-6xl flex-col gap-4">
            <section className="relative overflow-hidden rounded-[1.9rem] border border-white/70 bg-gradient-to-br from-sky-500 via-blue-600 to-blue-800 px-4 py-4 text-white shadow-2xl shadow-sky-900/20 sm:px-5">
                <div className="pointer-events-none absolute -right-16 -top-20 h-40 w-40 rounded-full bg-white/20 blur-3xl" />
                <div className="pointer-events-none absolute -bottom-20 left-10 h-36 w-36 rounded-full bg-cyan-300/20 blur-3xl" />
                <div className="relative">
                    <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-white/15 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-sky-100 ring-1 ring-white/20 backdrop-blur">
                        <FiUser className="h-3.5 w-3.5" />
                        My Profile
                    </div>
                    <h1 className="text-xl font-bold tracking-tight sm:text-2xl">Profile settings</h1>
                    <p className="mt-1.5 max-w-2xl text-sm leading-5 text-sky-100">
                        Update your personal details and keep your account identity current.
                    </p>
                </div>
            </section>

            <form onSubmit={updateProfile} className="grid gap-4 lg:grid-cols-[320px_minmax(0,1fr)]">
                <aside className="rounded-[1.75rem] border border-white/80 bg-white/95 p-5 text-center shadow-xl shadow-sky-900/5 backdrop-blur">
                    <div className="relative mx-auto h-32 w-32">
                        <label htmlFor="upload-button" className="group block cursor-pointer">
                            <span className="absolute -inset-2 rounded-full bg-gradient-to-br from-sky-300/50 to-blue-500/30 blur-xl transition group-hover:opacity-80" />
                            {image.preview ? (
                                <img src={image.preview} alt="Profile preview" className="relative h-32 w-32 rounded-full border-4 border-white object-cover shadow-2xl shadow-sky-900/20 ring-1 ring-sky-100" />
                            ) : (
                                <img src={profile?.profile ? BaseUrl() + profile.profile : ""} role="button" alt="Profile" className="relative h-32 w-32 rounded-full border-4 border-white bg-sky-50 object-cover shadow-2xl shadow-sky-900/20 ring-1 ring-sky-100" />
                            )}
                            <span className="absolute bottom-1 right-1 flex h-10 w-10 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-500 to-blue-700 text-white shadow-lg shadow-sky-500/25 ring-4 ring-white transition group-hover:scale-105">
                                <FiCamera className="h-5 w-5" />
                            </span>
                        </label>
                    </div>
                    <input
                        type="file"
                        id="upload-button"
                        className="hidden"
                        onChange={handleChange}
                        accept="image/*"
                    />
                    <h2 className="mt-5 text-xl font-extrabold tracking-tight text-slate-950">{`${firstName} ${lastName}`.trim() || 'Your profile'}</h2>
                    <p className="mt-1 text-sm font-medium text-slate-500">{email}</p>
                    <div className="mt-5 rounded-2xl bg-sky-50 p-3 text-left ring-1 ring-sky-100">
                        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-sky-600">Profile photo</p>
                        <p className="mt-1 text-xs leading-5 text-slate-500">Click the avatar to upload a new image.</p>
                    </div>
                </aside>

                <section className="rounded-[1.75rem] border border-white/80 bg-white/95 p-4 shadow-xl shadow-sky-900/5 backdrop-blur sm:p-5">
                    <div className="mb-5 border-b border-slate-100 pb-4">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-sky-600">Personal Information</p>
                        <h2 className="mt-1 text-xl font-extrabold text-slate-950">Account details</h2>
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2">
                        <label className="block">
                            <span className="mb-2 block text-xs font-bold uppercase tracking-wide text-slate-600">First name</span>
                            <div className="relative">
                                <FiUser className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                                <input
                                    className={`w-full rounded-2xl border bg-slate-50 py-3 pl-12 pr-4 text-sm font-semibold text-slate-900 outline-none transition placeholder:text-slate-400 hover:border-sky-200 hover:bg-white focus:border-sky-400 focus:bg-white focus:ring-4 focus:ring-sky-100 ${errors.firstName ? 'border-red-300 focus:border-red-400 focus:ring-red-100' : 'border-slate-200'}`}
                                    type="text"
                                    placeholder="First name"
                                    required
                                    value={firstName}
                                    onChange={(e) => {
                                        const sanitizedValue = sanitizeName(e.target.value);
                                        setFirstName(sanitizedValue);
                                        setErrors((prev) => ({
                                            ...prev,
                                            firstName: sanitizedValue === e.target.value ? '' : NAME_INVALID_MESSAGE,
                                        }));
                                    }}
                                />
                            </div>
                            {errors.firstName ? <p className="mt-1.5 text-xs font-semibold text-red-600">{errors.firstName}</p> : null}
                        </label>

                        <label className="block">
                            <span className="mb-2 block text-xs font-bold uppercase tracking-wide text-slate-600">Last name</span>
                            <div className="relative">
                                <FiUser className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                                <input
                                    className={`w-full rounded-2xl border bg-slate-50 py-3 pl-12 pr-4 text-sm font-semibold text-slate-900 outline-none transition placeholder:text-slate-400 hover:border-sky-200 hover:bg-white focus:border-sky-400 focus:bg-white focus:ring-4 focus:ring-sky-100 ${errors.lastName ? 'border-red-300 focus:border-red-400 focus:ring-red-100' : 'border-slate-200'}`}
                                    type="text"
                                    required
                                    placeholder="Last name"
                                    value={lastName}
                                    onChange={(e) => {
                                        const sanitizedValue = sanitizeName(e.target.value);
                                        setLastName(sanitizedValue);
                                        setErrors((prev) => ({
                                            ...prev,
                                            lastName: sanitizedValue === e.target.value ? '' : NAME_INVALID_MESSAGE,
                                        }));
                                    }}
                                />
                            </div>
                            {errors.lastName ? <p className="mt-1.5 text-xs font-semibold text-red-600">{errors.lastName}</p> : null}
                        </label>

                        <label className="block">
                            <span className="mb-2 block text-xs font-bold uppercase tracking-wide text-slate-600">Mobile</span>
                            <div className="relative">
                                <FiPhone className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                                <input
                                    className={`w-full rounded-2xl border bg-slate-50 py-3 pl-12 pr-4 text-sm font-semibold text-slate-900 outline-none transition placeholder:text-slate-400 hover:border-sky-200 hover:bg-white focus:border-sky-400 focus:bg-white focus:ring-4 focus:ring-sky-100 ${errors.mobile ? 'border-red-300 focus:border-red-400 focus:ring-red-100' : 'border-slate-200'}`}
                                    type="tel"
                                    inputMode="numeric"
                                    maxLength={15}
                                    required
                                    placeholder="Mobile"
                                    value={mobile}
                                    onChange={(e) => {
                                        const sanitizedValue = sanitizeMobile(e.target.value);
                                        setMobile(sanitizedValue);
                                        setErrors((prev) => ({
                                            ...prev,
                                            mobile: sanitizedValue === e.target.value ? '' : 'Mobile number can contain digits only.',
                                        }));
                                    }}
                                />
                            </div>
                            {errors.mobile ? <p className="mt-1.5 text-xs font-semibold text-red-600">{errors.mobile}</p> : null}
                        </label>

                        <label className="block">
                            <span className="mb-2 block text-xs font-bold uppercase tracking-wide text-slate-600">Email address</span>
                            <div className="relative">
                                <FiMail className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                                <input
                                    disabled
                                    className="w-full cursor-not-allowed rounded-2xl border border-slate-200 bg-slate-100 py-3 pl-12 pr-4 text-sm font-semibold lowercase text-slate-500 outline-none"
                                    type="text"
                                    required
                                    placeholder="Email address"
                                    value={email}
                                    onChange={(e) => {
                                        setEmail(e.target.value)
                                    }}
                                />
                            </div>
                        </label>
                    </div>

                    <div className="mt-6 flex justify-end">
                        <button
                            type="submit"
                            className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-sky-500 to-blue-700 px-7 py-3 text-sm font-bold text-white shadow-lg shadow-sky-600/20 transition hover:-translate-y-0.5 hover:shadow-xl hover:shadow-sky-700/25 focus:outline-none focus:ring-4 focus:ring-sky-100 sm:w-auto"
                        >
                            <FiSave className="h-4 w-4" />
                            Update profile
                        </button>
                    </div>
                </section>
            </form>
        </main>
    )
}

export default HOC(Profile)
